import { Component, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Auth } from '@angular/fire/auth';
import { ShopPickStat, ShopQueryStat, ShopSummary } from '../../../../../src/types';
import { FooterComponent } from '../../components/footer/footer.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { ShopSearchComponent } from '../../components/shop-search/shop-search.component';
import { FirebaseService } from '../../services/firebase.service';
import { LanguageService } from '../../services/language.service';
import { errorKey, formatDate, formatNumber } from '../../utils/format';

/** How long "Copied" stays on the button. */
export const COPIED_MS = 2000;

interface ShopStats {
   shop: ShopSummary;
   top: ShopQueryStat[];
   notFound: ShopQueryStat[];
   /** How many different searches found nothing; on Free, all that is shown of the list. */
   notFoundCount: number;
   notFoundLocked: boolean;
   /** The products customers chose most, from search results and from similar products (Pro). */
   picks: ShopPickStat[];
   picksLocked: boolean;
}

/** What an owner puts on a product page to show similar products there. */
export const SIMILAR_SNIPPET = '<div data-oopsy-similar></div>';

/** Where an owner asks for Pro until there is billing. */
export const UPGRADE_MAILTO = 'mailto:oopsy@oopsy.app?subject=Oopsy%20Pro';

/**
 * /business/:shopId: one shop, ordered by what its owner comes for: its state
 * at a glance, what its customers search for (the most searched, what found
 * nothing, the products they chose: one card), then setup (a search to try, the
 * search line and the similar-products line; first until the widget works)
 * and the shop's catalogue, plan and removal.
 */
@Component({
   selector: 'app-shop-page',
   standalone: true,
   imports: [NavbarComponent, FooterComponent, TranslateModule, RouterModule, ShopSearchComponent, NgTemplateOutlet],
   templateUrl: './shop-page.component.html',
})
export class ShopPageComponent implements OnInit {
   stats: ShopStats | null = null;
   missing = false;
   refreshing = false;
   copied = false;
   copiedSimilar = false;
   readonly similarSnippet = SIMILAR_SNIPPET;
   errorKey = '';
   readonly upgradeMailto = UPGRADE_MAILTO;

   constructor(
      private auth: Auth,
      private firebaseService: FirebaseService,
      private route: ActivatedRoute,
      private router: Router,
      private language: LanguageService,
      private translate: TranslateService,
   ) {}

   get shopId(): string {
      return this.route.snapshot.paramMap.get('shopId') ?? '';
   }

   ngOnInit() {
      this.auth.onAuthStateChanged((user) => {
         if (!user) {
            this.router.navigate(['/login'], { queryParams: { next: `/business/${this.shopId}` } });
            return;
         }
         this.load();
      });
   }

   load() {
      this.firebaseService.getFunction('getShopStats')({ shopId: this.shopId }).then((result) => {
         this.stats = result.data as ShopStats;
      }).catch(() => {
         this.missing = true;
      });
   }

   /** The line the owner pastes into their site. */
   get snippet(): string {
      return `<script src="${location.origin}/widget.js" data-shop="${this.shopId}" defer></script>`;
   }

   copy() {
      navigator.clipboard?.writeText(this.snippet).then(() => {
         this.copied = true;
         setTimeout(() => (this.copied = false), COPIED_MS);
      }).catch(() => undefined);
   }

   copySimilar() {
      navigator.clipboard?.writeText(this.similarSnippet).then(() => {
         this.copiedSimilar = true;
         setTimeout(() => (this.copiedSimilar = false), COPIED_MS);
      }).catch(() => undefined);
   }

   /** The widget has searched from the shop's site: setup moves below what customers search for. */
   get installed(): boolean {
      return !!this.stats?.shop.lastWidgetSearchAt;
   }

   num(value: number): string {
      return formatNumber(value, this.language.current);
   }

   /** How the catalogue stays current: read again every night, every week, or only by hand. */
   refreshText(days: number): string {
      if (days === 1) return 'pages.shop-page.catalogue.text';
      if (days > 1) return 'pages.shop-page.catalogue.textWeekly';
      return 'pages.shop-page.catalogue.textManual';
   }

   date(millis: number | null): string {
      return formatDate(millis, this.language.current);
   }

   refresh() {
      if (this.refreshing) return;
      this.refreshing = true;
      this.errorKey = '';
      this.firebaseService.getFunction('refreshShop')({ shopId: this.shopId }).then((result) => {
         if (this.stats) this.stats = { ...this.stats, shop: result.data as ShopSummary };
      }).catch((error) => {
         this.errorKey = errorKey(error);
      }).finally(() => {
         this.refreshing = false;
      });
   }

   remove() {
      if (!confirm(this.translate.instant('pages.shop-page.removeConfirm'))) return;
      this.firebaseService.getFunction('removeShop')({ shopId: this.shopId }).then(() => {
         this.router.navigate(['/business']);
      }).catch((error) => {
         this.errorKey = errorKey(error);
      });
   }
}
